import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { isEmpty } from 'validate.js';

import VerificationCodeInput from 'dpl/common/components/VerificationCodeInput';
import { ANALYTICS_EVENTS, ANALYTICS_VIEWS } from 'dpl/constants/analytics';
import { TOAST_NOTIFICATIONS_TYPES } from 'dpl/shared/constants/shared';
import useContactContext from 'dpl/contacts/hooks/useContactContext';
import useContactVerification from 'dpl/contacts/hooks/useContactVerification';
import {
  VERIFICATION_CHANNELS,
  VERIFICATION_CHANNEL_NAMES
} from 'dpl/contacts/utils/constants';
import useFormContext from 'dpl/shared/form/hooks/useFormContext';
import useToastNotifications from 'dpl/shared/hooks/useToastNotifications';
import { sendAnalyticsEvent } from 'dpl/util/analytics';

const ALT_VERIFICATION_CTAS = {
  [VERIFICATION_CHANNELS.SMS]: 'get verified via email',
  [VERIFICATION_CHANNELS.EMAIL]: 'get verified via phone'
};

/** @param {any} props */
export default function VerificationCodeQuestion({
  name,
  phone_number: phoneNumber = null,
  email,
  isCurrentSection,
  isAnswered = false
}) {
  const { setValue, formFieldValue, formFieldErrors } = useFormContext(name);
  const { sendCode } = useContactVerification();
  const { eventProps } = useContactContext();
  const { pushToastNotification } = useToastNotifications();

  const channel = formFieldValue?.channel;
  const code = formFieldValue?.code || '';
  const channelName = VERIFICATION_CHANNEL_NAMES[channel];

  function setCode(newCode) {
    setValue(`${name}.code`, newCode);
  }

  function trackButtonClick(e) {
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      ...eventProps,
      view: ANALYTICS_VIEWS.CONTACT_QUIZ,
      button_cta: e.target.textContent
    });
  }

  function handleCodeRequest(e) {
    if (isAnswered) {
      pushToastNotification({
        type: TOAST_NOTIFICATIONS_TYPES.INFO,
        message:
          "You've already answered this one—go ahead to the next question."
      });
      return;
    }

    if (!isEmpty(e?.target)) trackButtonClick(e);

    if (isCurrentSection && formFieldValue?.channel) {
      const { code: _code, ...recipient } = formFieldValue;
      setCode('');
      sendCode(recipient);
    }
  }

  function handleAltChannelPress(e) {
    if (!isEmpty(e?.target)) trackButtonClick(e);

    const altChannelData =
      channel === VERIFICATION_CHANNELS.SMS
        ? { channel: VERIFICATION_CHANNELS.EMAIL, email }
        : { channel: VERIFICATION_CHANNELS.SMS, phone_number: phoneNumber };

    setValue(name, { ...altChannelData, code: '' });
  }

  // Send verification code when section is active or when the channel is changed
  useEffect(() => {
    if (!isCurrentSection || !channel) return;

    handleCodeRequest();
  }, [isCurrentSection, channel]);

  // Clear code input when server returns a verification error
  useEffect(() => {
    if (formFieldErrors?.length) {
      setCode('');
    }
  }, [formFieldErrors]);

  if (!channel) return null;

  return (
    <div className="VerificationCodeQuestion QuestionLayout tc">
      <div className="mb8 mb14-md tc">
        <h3 className="f4 fw-medium f5-md">Verify your {channelName}</h3>
        <p className="mt2 mt4-md font-16 secondary">
          We sent you a verification code to your {channelName}.
        </p>
      </div>
      <VerificationCodeInput value={code} onChange={setCode} />
      <p className="font-16 secondary mt6 tc">
        Didn’t receive a code?{' '}
        <button type="button" className="underline" onClick={handleCodeRequest}>
          Resend code
        </button>
        {phoneNumber && (
          <>
            {' or '}
            <br />
            <button
              type="button"
              className="underline"
              onClick={handleAltChannelPress}
            >
              {ALT_VERIFICATION_CTAS[channel]}
            </button>
          </>
        )}
      </p>
    </div>
  );
}

VerificationCodeQuestion.propTypes = {
  name: PropTypes.string.isRequired,
  phone_number: PropTypes.string,
  email: PropTypes.string.isRequired,
  isCurrentSection: PropTypes.bool.isRequired,
  isAnswered: PropTypes.bool
};
